import { useState, useEffect, useMemo, useCallback } from "react";
import { ordersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Users,
  Search,
  Download,
  Upload,
  Phone,
  Mail,
  MapPin,
  Package,
  FileText,
  Scan,
  Eye,
  X,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const Customers = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  // Label extraction
  const [showExtractDialog, setShowExtractDialog] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Customer detail
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      // Load orders in batches for better performance
      // First load 500, which covers most active customers
      const res = await ordersAPI.getAll({ limit: 1000 });
      let ordersList = [];
      if (Array.isArray(res.data)) {
        ordersList = res.data;
      } else if (res.data?.orders) {
        ordersList = res.data.orders;
      }
      setOrders(ordersList);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
      console.error(error);
    }
    setLoading(false);
  };

  // Extract unique customers from orders
  const customers = useMemo(() => {
    const customerMap = {};
    
    orders.forEach(order => {
      const name = order.customer_name?.trim();
      if (!name || name === 'N/A') return;
      
      const key = name.toLowerCase();
      
      if (!customerMap[key]) {
        customerMap[key] = {
          name: name,
          email: order.customer_email || '',
          phone: order.customer_phone || '',
          address: order.customer_address || '',
          ordersCount: 0,
          totalSpent: 0,
          lastOrderDate: order.created_at,
          orders: [],
        };
      }
      
      // Update with latest info
      if (order.customer_email && !customerMap[key].email) {
        customerMap[key].email = order.customer_email;
      }
      if (order.customer_phone && !customerMap[key].phone) {
        customerMap[key].phone = order.customer_phone;
      }
      if (order.customer_address && (!customerMap[key].address || customerMap[key].address.length < order.customer_address.length)) {
        customerMap[key].address = order.customer_address;
      }
      
      customerMap[key].ordersCount += 1;
      customerMap[key].totalSpent += order.total_amount || 0;
      customerMap[key].orders.push(order);
      
      // Update last order date
      if (new Date(order.created_at) > new Date(customerMap[key].lastOrderDate)) {
        customerMap[key].lastOrderDate = order.created_at;
      }
    });
    
    return Object.values(customerMap).sort((a, b) => 
      new Date(b.lastOrderDate) - new Date(a.lastOrderDate)
    );
  }, [orders]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers;
    
    const query = searchQuery.toLowerCase();
    return customers.filter(c =>
      c.name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.phone?.includes(query) ||
      c.address?.toLowerCase().includes(query)
    );
  }, [customers, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCustomers.slice(start, start + itemsPerPage);
  }, [filteredCustomers, currentPage]);

  // Export to CSV
  const handleExport = () => {
    const headers = ["Nome", "Email", "Telefono", "Indirizzo", "N. Ordini", "Totale Speso", "Ultimo Ordine"];
    const rows = filteredCustomers.map(c => [
      c.name,
      c.email,
      c.phone,
      c.address?.replace(/,/g, ' '),
      c.ordersCount,
      `€${c.totalSpent.toFixed(2)}`,
      new Date(c.lastOrderDate).toLocaleDateString('it-IT')
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clienti_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success(`Esportati ${filteredCustomers.length} clienti`);
  };

  // Handle file upload for label extraction
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      extractDataFromLabel(file);
    }
  };

  // Extract data from shipping label (Poste Italiane format)
  const extractDataFromLabel = async (file, saveToDb = false) => {
    setExtracting(true);
    setExtractedData(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      
      // Check if it's a PDF file - use the FREE PDF extraction
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        const formData = new FormData();
        formData.append('file', file);
        
        // Add save_to_db parameter if requested
        const url = saveToDb 
          ? `${API_URL}/api/ocr/extract-pdf?save_to_db=true`
          : `${API_URL}/api/ocr/extract-pdf`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        const data = await response.json();
        
        if (data.success && data.customers && data.customers.length > 0) {
          setExtractedData({
            customers: data.customers,
            total: data.total,
            saved: data.saved || 0,
            message: data.message,
          });
          
          if (saveToDb && data.saved > 0) {
            toast.success(`✅ ${data.saved} clienti salvati nel database!`);
          } else {
            toast.success(`📋 Estratti ${data.total} clienti dal PDF`);
          }
        } else {
          setExtractedData({
            customers: [],
            total: 0,
            error: data.error || "Nessun cliente trovato nel PDF",
          });
          toast.warning(data.error || "Nessun cliente trovato");
        }
        setExtracting(false);
        return;
      }
      
      // For images, show error
      toast.error("Carica un file PDF di etichette Poste Italiane");
      setExtractedData({
        customers: [],
        total: 0,
        error: "Formato non supportato. Carica un file PDF.",
      });
      setExtracting(false);
      
    } catch (error) {
      console.error("Extraction error:", error);
      toast.error("Errore nell'estrazione dati");
      setExtractedData({
        customers: [],
        total: 0,
        error: "Errore di connessione. Riprova.",
      });
      setExtracting(false);
    }
  };
  
  // Save all extracted customers to database
  const saveAllCustomersToDb = async () => {
    if (!uploadedFile) return;
    
    setExtracting(true);
    await extractDataFromLabel(uploadedFile, true);
  };

  // Handle create order from extraction (legacy - not used)
  const handleCreateOrderFromExtraction = () => {
    toast.info("Usa il pulsante 'Salva Tutti nel DB' per salvare i clienti");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#C9A227] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="customers-page" className="animate-fadeIn">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-gray-900 flex items-center gap-3">
              <Users className="w-8 h-8 text-[#C9A227]" />
              Database Clienti
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {customers.length} clienti totali • {filteredCustomers.length} filtrati
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowExtractDialog(true)}
              className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              <Scan className="w-4 h-4 mr-2" />
              Estrai da Etichetta
            </Button>
            <Button 
              variant="outline"
              onClick={handleExport}
              className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV
            </Button>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Cerca per nome, email, telefono, indirizzo..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="pl-10"
            data-testid="customer-search"
          />
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">Cliente</TableHead>
              <TableHead className="font-semibold">Contatti</TableHead>
              <TableHead className="font-semibold">Indirizzo</TableHead>
              <TableHead className="font-semibold text-center">Ordini</TableHead>
              <TableHead className="font-semibold text-right">Totale</TableHead>
              <TableHead className="font-semibold">Ultimo Ordine</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-400">
                  Nessun cliente trovato
                </TableCell>
              </TableRow>
            ) : (
              paginatedCustomers.map((customer, idx) => (
                <TableRow key={idx} className="hover:bg-gray-50">
                  <TableCell>
                    <span className="font-medium text-gray-900">{customer.name}</span>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {customer.email && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Mail className="w-3 h-3" />
                          <span className="truncate max-w-[150px]">{customer.email}</span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone className="w-3 h-3" />
                          {customer.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-start gap-1 text-sm text-gray-600 max-w-[200px]">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span className="truncate">{customer.address || 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{customer.ordersCount}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-medium text-[#C9A227]">
                      €{customer.totalSpent.toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500">
                      {new Date(customer.lastOrderDate).toLocaleDateString('it-IT')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCustomer(customer)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-500">
              Pagina {currentPage} di {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Customer Detail Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#C9A227]" />
              {selectedCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <p className="font-medium">{selectedCustomer.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Telefono</p>
                  <p className="font-medium">{selectedCustomer.phone || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-gray-500 uppercase">Indirizzo</p>
                <p className="font-medium">{selectedCustomer.address || 'N/A'}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#C9A227]">{selectedCustomer.ordersCount}</p>
                  <p className="text-xs text-gray-500">Ordini</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#C9A227]">€{selectedCustomer.totalSpent.toFixed(0)}</p>
                  <p className="text-xs text-gray-500">Totale Speso</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-700">
                    {new Date(selectedCustomer.lastOrderDate).toLocaleDateString('it-IT')}
                  </p>
                  <p className="text-xs text-gray-500">Ultimo Ordine</p>
                </div>
              </div>
              
              {/* Recent orders */}
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500 uppercase mb-2">Ultimi Ordini</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {selectedCustomer.orders.slice(0, 5).map((order, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium">
                          {order.items?.map(i => i.perfume_code).join(', ') || 'N/A'}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          {new Date(order.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                      <span className="font-medium text-[#C9A227]">€{order.total_amount?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Label Extraction Dialog */}
      <Dialog open={showExtractDialog} onOpenChange={setShowExtractDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-purple-600" />
              Estrai Clienti da PDF Etichette Poste
            </DialogTitle>
            <DialogDescription>
              Carica un PDF di etichette Poste Italiane per estrarre automaticamente tutti i clienti
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload area */}
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="label-upload"
              />
              <label htmlFor="label-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="font-medium text-gray-700">
                  {uploadedFile ? uploadedFile.name : "Clicca per caricare un PDF di etichette"}
                </p>
                <p className="text-sm text-gray-500 mt-1">Solo file PDF (gratuito, senza crediti AI)</p>
              </label>
            </div>
            
            {extracting && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-600 mr-3" />
                <span className="text-lg">Estrazione clienti in corso...</span>
              </div>
            )}
            
            {/* Extracted customers list */}
            {extractedData && !extracting && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div>
                    <p className="font-semibold text-purple-800">
                      📋 {extractedData.total} clienti estratti
                    </p>
                    {extractedData.saved > 0 && (
                      <p className="text-sm text-green-600">
                        ✅ {extractedData.saved} salvati nel database
                      </p>
                    )}
                    {extractedData.error && (
                      <p className="text-sm text-red-600">{extractedData.error}</p>
                    )}
                  </div>
                  {extractedData.total > 0 && !extractedData.saved && (
                    <Button 
                      onClick={saveAllCustomersToDb}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Salva Tutti nel DB
                    </Button>
                  )}
                </div>
                
                {/* Customer list */}
                {extractedData.customers && extractedData.customers.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-8">#</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Telefono</TableHead>
                            <TableHead>Indirizzo</TableHead>
                            <TableHead>Città</TableHead>
                            <TableHead className="w-16">Stato</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {extractedData.customers.map((customer, idx) => (
                            <TableRow key={idx} className={customer.saved ? 'bg-green-50' : customer.exists ? 'bg-yellow-50' : ''}>
                              <TableCell className="text-xs text-gray-500">{customer.page || idx + 1}</TableCell>
                              <TableCell className="font-medium">{customer.nome || '-'}</TableCell>
                              <TableCell className="text-sm">{customer.telefono || '-'}</TableCell>
                              <TableCell className="text-sm max-w-[150px] truncate">{customer.indirizzo || '-'}</TableCell>
                              <TableCell className="text-sm">
                                {customer.cap} {customer.citta} {customer.provincia ? `(${customer.provincia})` : ''}
                              </TableCell>
                              <TableCell>
                                {customer.saved ? (
                                  <Badge className="bg-green-100 text-green-700">Salvato</Badge>
                                ) : customer.exists ? (
                                  <Badge className="bg-yellow-100 text-yellow-700">Esiste</Badge>
                                ) : (
                                  <Badge variant="outline">Nuovo</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowExtractDialog(false);
              setExtractedData(null);
              setUploadedFile(null);
            }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
